import React, { useState } from "react";
import { TextField, Typography, Box, InputAdornment, MenuItem, IconButton } from "@mui/material";
import { FormikProps } from "formik";
import { BasicDetailsFormProps } from "@utils/types";
import { Visibility, VisibilityOff } from "@mui/icons-material"; 

interface DynamicInputProps {
  formik: FormikProps<BasicDetailsFormProps>;
  name: keyof BasicDetailsFormProps;
  label: string;
  type: string;
  placeholder?: string;
  helperText?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  adornment?: {
    text: string;
    onClick: () => void;
  };
  options?: { value: string; label: string }[];
  verified?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
  maxLength?: number;
}

const DynamicInput: React.FC<DynamicInputProps> = ({
  formik,
  name,
  label,
  type,
  placeholder,
  helperText,
  inputProps,
  adornment,
  options,
  verified,
  value,
  disabled,
  onChange,
  maxLength,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box display="flex" flexDirection="column" sx={{ width: { xs: "100%", lg: "50%" } }} mt={1}>
      <Typography variant="h6" mb={1}
       sx={{
        color:"#474747",
        fontFamily: "Maven Pro, sans-serif",
        fontSize: "20px",
        fontWeight: 500, 
        lineHeight: "30px", 
        textAlign: "left", 
        textUnderlinePosition: "from-font", 
        textDecorationSkipInk: "none", 
      }}
      >
        {label}
      </Typography>
      {type === "select" && options ? (
        <TextField
          select
          name={name}
          value={value ?? formik.values[name]}
          disabled={disabled ?? Boolean(value)} 
          onBlur={formik.handleBlur}
          error={Boolean(formik.touched[name] && formik.errors[name])}
          helperText={
            formik.touched[name] && formik.errors[name]
              ? formik.errors[name]
              : helperText || ""
          }
          onChange={(e) =>
            (onChange && onChange(e)) ??
            formik.setFieldValue(name, e.target.value)
          }
          sx={{
            fieldset: {
              borderColor: "#DADADA",
            },
            width: { xs: "100%", lg: "100%" },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          name={name}
          type={type === "password" && showPassword ? "text" : type}
          placeholder={placeholder}
          value={value ?? formik.values[name]}
          disabled={disabled ?? Boolean(value)} 
          onChange={(e) => {
            let value = e.target.value;
            if (type === "text" && inputProps?.pattern) {
              const regex = new RegExp(inputProps.pattern);
              if (!regex.test(value)) value = value.replace(/[^0-9]/g, "");
            }
            formik.setFieldValue(name, value);
          }}
          onBlur={formik.handleBlur}
          error={Boolean(formik.touched[name] && formik.errors[name])}
          helperText={
            formik.touched[name] && formik.errors[name]
              ? formik.errors[name]
              : helperText || ""
          }
          sx={{
            fieldset: {
              borderColor: "#DADADA",
            },
            width: { xs: "100%", lg: "100%" },
          }}
          slotProps={{
            htmlInput: {
              maxLength: maxLength,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {type === "password" && (
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="start"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )}
                {verified ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mr: 1
                    }}
                  >
                   <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 2.75C7.66498 2.75 6.35994 3.14588 5.2499 3.88758C4.13987 4.62928 3.27471 5.68348 2.76382 6.91689C2.25292 8.15029 2.11925 9.50749 2.3797 10.8169C2.64015 12.1262 3.28303 13.329 4.22703 14.273C5.17104 15.217 6.37377 15.8598 7.68314 16.1203C8.99251 16.3807 10.3497 16.2471 11.5831 15.7362C12.8165 15.2253 13.8707 14.3601 14.6124 13.2501C15.3541 12.1401 15.75 10.835 15.75 9.5C15.748 7.71039 15.0362 5.99464 13.7708 4.7292C12.5054 3.46375 10.7896 2.75197 9 2.75ZM11.5022 8.62722L8.80223 11.3272C8.67565 11.4538 8.50399 11.5249 8.325 11.5249C8.14602 11.5249 7.97436 11.4538 7.84778 11.3272L6.49778 9.97722C6.37482 9.84992 6.30678 9.67941 6.30832 9.50243C6.30986 9.32545 6.38085 9.15615 6.506 9.031C6.63115 8.90585 6.80045 8.83486 6.97743 8.83332C7.15441 8.83178 7.32492 8.89982 7.45223 9.02277L8.325 9.89555L10.5478 7.67277C10.6751 7.54982 10.8456 7.48178 11.0226 7.48332C11.1996 7.48486 11.3689 7.55585 11.494 7.681C11.6192 7.80615 11.6901 7.97545 11.6917 8.15243C11.6932 8.32941 11.6252 8.49992 11.5022 8.62722Z" fill="#31C48D" />
                    </svg>

                    <Typography
                      sx={{
                        color: '#11A64A',
                        fontSize: '15px',
                        fontWeight: 500,
                        fontFamily: "Maven Pro, sans-serif",
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      Verified
                    </Typography>
                  </Box>
                ) : null}
                {adornment && (
                  <Typography
                    onClick={adornment.onClick}
                    className="custom-button"
                    color="text.secondary"
                  >
                    {adornment.text}
                  </Typography>
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default DynamicInput;